import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, Eye, Clapperboard, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { UserAvatar, VerifiedTick } from "@/components/UserAvatar";
import { UploadReelDialog } from "@/components/UploadReelDialog";
import { Comments } from "@/components/PostCard";
import { Spinner, EmptyState } from "@/components/Loader";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { reelVideo, reelTitle } from "@/components/ReelThumb";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const base = (r) => (r.source === "post" ? `/posts/${r.id}` : `/reels/${r.id}`);

function ReelItem({ reel: initial, onComments }) {
  const ref = useRef();
  const viewed = useRef(false);
  const [reel, setReel] = useState(initial);
  const { user } = useAuth();

  useEffect(() => {
    const el = ref.current;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.play().catch(() => {});
        if (!viewed.current && user) { viewed.current = true; api.post(`${base(reel)}/view`).catch(() => {}); }
      } else {
        el.pause();
      }
    }, { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [reel, user]);

  const like = async () => {
    if (!user) return toast.info("Log in to like reels");
    const liked = !reel.liked;
    setReel((r) => ({ ...r, liked, likes_count: (r.likes_count || 0) + (liked ? 1 : -1) }));
    await (liked ? api.post(`${base(reel)}/like`) : api.delete(`${base(reel)}/like`)).catch(() => {});
  };

  return (
    <section id={reel.id} className="flex h-[calc(100vh-9rem)] snap-start items-center justify-center py-3 lg:h-[calc(100vh-6.5rem)]" data-testid={`reel-item-${reel.id}`}>
      <div className="relative aspect-[9/16] h-full max-w-full overflow-hidden rounded-3xl bg-black shadow-2xl">
        <video ref={ref} src={reelVideo(reel)} loop muted playsInline controls className="h-full w-full object-cover" data-testid={`reel-video-${reel.id}`} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-5 pb-16 pr-16 text-white">
          <Link to={`/profile/${reel.author?.id}`} className="pointer-events-auto flex items-center gap-2">
            <UserAvatar user={reel.author} size="sm" /><span className="text-sm font-semibold">{reel.author?.full_name}</span><VerifiedTick user={reel.author} />
          </Link>
          <p className="mt-3 font-display text-base font-bold leading-snug">{reelTitle(reel)}</p>
          {reel.category && <span className="mt-2 inline-block rounded-full bg-tm-gold px-2.5 py-0.5 text-[11px] font-semibold text-white">{reel.category}</span>}
        </div>
        <div className="absolute bottom-24 right-3 flex flex-col items-center gap-4 text-white">
          <button onClick={like} className="flex flex-col items-center" data-testid={`reel-like-button-${reel.id}`}>
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white/15 backdrop-blur-md"><Heart className={`h-5 w-5 ${reel.liked ? "fill-red-500 text-red-500" : ""}`} /></span>
            <span className="mt-1 text-xs" data-testid={`reel-like-count-${reel.id}`}>{reel.likes_count || 0}</span>
          </button>
          <button onClick={() => onComments(reel)} className="flex flex-col items-center" data-testid={`reel-comment-button-${reel.id}`}>
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white/15 backdrop-blur-md"><MessageCircle className="h-5 w-5" /></span>
            <span className="mt-1 text-xs">{reel.comments_count || 0}</span>
          </button>
          <div className="flex flex-col items-center"><span className="grid h-11 w-11 place-items-center rounded-full bg-white/15 backdrop-blur-md"><Eye className="h-5 w-5" /></span><span className="mt-1 text-xs">{reel.views_count ?? reel.views ?? 0}</span></div>
        </div>
      </div>
    </section>
  );
}

export default function Reels() {
  const [reels, setReels] = useState(null);
  const [commentsFor, setCommentsFor] = useState(null);
  const [params] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reels").then(({ data }) => setReels(data)).catch(() => setReels([]));
  }, []);

  useEffect(() => {
    const r = params.get("r");
    if (reels && r) document.getElementById(r)?.scrollIntoView();
  }, [reels, params]);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <div><p className="tm-eyebrow">Reels & videos</p><h1 className="mt-1 font-display text-2xl font-extrabold text-tm-green">Learn tax in 60 seconds</h1></div>
        {user ? <UploadReelDialog /> : (
          <button onClick={() => navigate("/login")} className="rounded-full bg-tm-green px-5 py-2.5 text-sm font-semibold text-white" data-testid="reels-login-to-upload">Log in to upload</button>
        )}
      </div>
      {reels === null ? <Spinner /> : reels.length === 0 ? (
        <div className="mt-6"><EmptyState icon={Clapperboard} title="No reels live right now" text="New videos appear here after admin approval." testid="reels-empty" /></div>
      ) : (
        <div className="no-scrollbar mt-4 h-[calc(100vh-9rem)] snap-y snap-mandatory overflow-y-auto lg:h-[calc(100vh-6.5rem)]" data-testid="reels-feed">
          {reels.map((r) => <ReelItem key={r.id} reel={r} onComments={setCommentsFor} />)}
        </div>
      )}
      <Sheet open={!!commentsFor} onOpenChange={(o) => !o && setCommentsFor(null)}>
        <SheetContent className="overflow-y-auto" data-testid="reel-comments-sheet">
          <SheetHeader><SheetTitle className="font-display text-tm-green">Comments</SheetTitle></SheetHeader>
          {commentsFor && <Comments base={base(commentsFor)} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
